import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigation,
} from "react-router";
import { useEffect } from "react";
import { analyticsAllowedFor } from "~/utils/analyticsPolicy";

import type { Route } from "./+types/root";
import "../styles/tokens.css";
import "../styles/themes/schoolconferencego.css";
import "./styles/fonts.css";
import "./app.css";
import { LanguageProvider } from "./lib/i18n";
import { FeedbackButton } from "~/components/features";
import { CookieConsent } from "./components/CookieConsent";
import { ACCENT_HEX } from "./styles/brand";
import { ToastProvider } from "./components/Toast";
import { captureException, SentryErrorBoundary } from "./lib/sentry";
import { NotFoundPage } from "~/components/NotFoundPage";

export const meta: Route.MetaFunction = () => [
  { title: "SchoolConferenceGo · Conference night, without the chaos" },
  { name: "description", content: "Build your entire conference schedule, preview exactly what families will see, and send yourself a test invite. Pay only when you're ready to publish." },
  { property: "og:type", content: "website" },
  { property: "og:site_name", content: "School Conference Go" },
  { property: "og:title", content: "SchoolConferenceGo · Conference night, without the chaos" },
  { property: "og:description", content: "Build your entire conference schedule, preview exactly what families will see, and send yourself a test invite. Pay only when you're ready to publish." },
  { property: "og:url", content: "https://schoolconferencego.com" },
  { property: "og:image", content: "https://schoolconferencego.com/og-image.jpg" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: "SchoolConferenceGo · Conference night, without the chaos" },
  { name: "twitter:description", content: "Build your entire conference schedule, preview exactly what families will see, and send yourself a test invite. Pay only when you're ready to publish." },
  { name: "twitter:image", content: "https://schoolconferencego.com/og-image.jpg" },
];

export const links: Route.LinksFunction = () => [
  {
    rel: "preload",
    href: "/fonts/Capriola%5Bwght%5D.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/fonts/Figtree-latin.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
  { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
  { rel: "icon", type: "image/png", href: "/favicon.png" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  { rel: "shortcut icon", href: "/favicon.ico" },
  { rel: "manifest", href: "/site.webmanifest" },
];


export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const analyticsAllowed = analyticsAllowedFor(location.pathname);

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content={ACCENT_HEX} />

        {analyticsAllowed && (
          <>
            {/* Google Analytics - Load with consent mode */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  // Set default consent to denied, will be updated when user consents
                  gtag('consent', 'default', {
                    'analytics_storage': 'denied',
                    'ad_storage': 'denied',
                    'wait_for_update': 500
                  });
                `,
              }}
            />
            <script async src="https://www.googletagmanager.com/gtag/js?id=G-7V9TJCSG2E"></script>
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  gtag('config', 'G-7V9TJCSG2E');
                `,
              }}
            />

            {/* Session replay is injected by CookieConsent after consent, and
                only on paths analyticsAllowedFor() permits. */}
          </>
        )}

        <Meta />
        <Links />
      </head>
      <body>
        <LanguageProvider>
          <ToastProvider>
            {children}
          </ToastProvider>
        </LanguageProvider>
        <ScrollRestoration />
        <Scripts />
        {/* Mobile Text Alerts conversion tracking — marketing pages only, same
            rule as the trackers above: it must not run where student or family
            data is on screen. */}
        {analyticsAllowed && (
          <>
            <div id="mta-tracking" data-account="SchoolConferenceGo"></div>
            <script src="https://platform.mobile-text-alerts.com/widgets/mta-ct.js?tk=74"></script>
          </>
        )}
      </body>
    </html>
  );
}

function NavigationProgress() {
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';

  if (!isNavigating) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] h-1 bg-blue-100">
      <div className="h-full bg-blue-600 animate-pulse" style={{ width: '100%', animationDuration: '1s' }} />
    </div>
  );
}

export default function App() {
  return (
    <>
      <NavigationProgress />
      <Outlet />
      <FeedbackButton />
      <CookieConsent />
    </>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Something went sideways";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  let is404 = false;

  // Capture error to Sentry (only for non-404/429 errors)
  useEffect(() => {
    if (error && !isRouteErrorResponse(error)) {
      captureException(error);
    } else if (isRouteErrorResponse(error) && error.status >= 500) {
      captureException(new Error(`HTTP ${error.status}: ${error.statusText}`), {
        status: error.status,
        data: error.data
      });
    }
  }, [error]);

  if (isRouteErrorResponse(error)) {
    is404 = error.status === 404;
    message = error.status === 404 ? "404" : error.status === 429 ? "Rate Limited" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.status === 429
        ? error.data || "Too many requests. Please wait a moment and try again."
        : error.data || error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  if (is404) {
    return <NotFoundPage />;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
